/**
 * Training SOP — Foundations.
 *
 * The concepts everyone needs regardless of role. These come first in both the
 * in-app page and the Word export, because almost every support question traces
 * back to one of them: "why can't I edit this job", "why did the numbers vanish",
 * "why does it say at risk", "why did my export have 137 rows".
 *
 * Every statement here was verified against the code (2026-07-25 sweep). Where a
 * behavior is a deliberate design decision rather than an accident, say so — it
 * stops people filing it as a bug.
 */
import { OWNERSHIP } from './types';
import type { Section } from './types';

export const FOUNDATIONS: Section[] = [
  {
    id: 'getting-around',
    title: 'Getting around',
    intro: 'Two levels of navigation: company-wide tabs across the top, and a per-job rail inside a project.',
    topics: [
      {
        id: 'two-levels',
        title: 'Company-wide tabs vs. inside a job',
        summary:
          'The tabs across the top move between company-wide areas — the whole portfolio, all equipment, all QA/QC. Opening a single job from Projects puts you in that job\'s workspace, which has its own navigation down the left side.',
        steps: [
          'Start on **Projects** — this is the job list and the way into everything job-specific.',
          'Click any row (or **Open**) to enter that job\'s workspace.',
          'Use the left rail to move between that job\'s tabs, grouped as Field, Procurement, Documents and Controls.',
          'Use the breadcrumb at the top to get back to the portfolio.',
        ],
        watchOut: [
          'On a phone or small tablet the left rail becomes a scrolling tab bar across the top, and the company-wide tabs collapse behind a **Menu** button.',
          'You only see tabs your access allows, so your rail may be shorter than a colleague\'s. That is expected, not a fault.',
        ],
        deepLink: '/projects',
        // A field leader has one tab; the other half of this topic describes
        // navigation they will never see, and their own track covers what they do.
        access: { hideFromFieldLeaderOnly: true },
      },
      {
        id: 'my-vs-all',
        title: 'My Projects vs. All',
        summary:
          'The toggle in the filter bar switches between jobs you are personally attached to and every job you are allowed to see. Your choice is remembered the next time you open Projects.',
        steps: [
          'On **Projects**, use the **My Projects / All** toggle at the left of the filter bar.',
          '**My Projects** shows jobs where you are the PM, secondary PM, estimator, project engineer, safety manager, or an assigned field leader.',
          '**All** widens it to everything your access permits — which may still be limited to your division.',
        ],
        watchOut: [
          'If you never see this toggle, your access is limited to your own assignments — there is nothing wider to switch to.',
          'The KPI tiles above the list follow the toggle, so the counts change when you switch.',
          'Unlike the filters next to it, this choice is a real preference: it survives closing the browser, and **Clear filters** does not reset it.',
        ],
        deepLink: '/projects',
        // The toggle only appears when there is a wider set to switch to. A
        // field-leader-only user has none, so this would describe a control
        // that is not on their screen.
        access: { hideFromFieldLeaderOnly: true },
      },
      {
        id: 'sticky-filters',
        title: 'Filters stay put while you work',
        summary:
          'Filter the Projects list once and it stays filtered while you open jobs and come back, so you can work through a set without rebuilding it every time. It resets itself when you close the browser.',
        steps: [
          'Narrow the list with the search box, the status, division and mobilization filters, the start-date range, or **Show archived**.',
          'Open a job, do the work, come back — the list is still narrowed the same way.',
          'Use **Clear filters** to undo all of it in one click. The button only appears when something is actually filtering.',
        ],
        watchOut: [
          'A list can therefore arrive **already narrowed** by something you set an hour ago. If a job you expect is missing, look for **Clear filters** before concluding it is gone.',
          'Filters last for the browser session only, and are deliberately not remembered until tomorrow — coming back to a list silently filtered by something you set last week would be its own problem.',
          '**Clear filters** leaves the sort order alone. Sorting frames the list rather than hides rows from it, so there is nothing to undo.',
        ],
        deepLink: '/projects',
        // NOT hidden from field leaders. Only the My/All toggle is conditional
        // in ProjectsPage — the search box, the status, division, mobilization
        // and date filters, Show archived and Clear filters all render for
        // everyone, and all of them persist. A field leader who filters their
        // own jobs, opens one and comes back meets exactly the same missing-job
        // surprise, so hiding this left the one reader with no other guidance
        // holding nothing (review, #317). The My/All-specific nuance lives on
        // `my-vs-all` instead, which is hidden from them for a real reason.
      },
    ],
  },

  {
    id: 'job-numbers',
    title: 'Job numbers, and what they lock',
    intro:
      'Jobs arrive from Bid Tracker before Sage Intacct has issued a real number. The placeholder prefix controls what you can do.',
    topics: [
      {
        id: 'job-number-locks',
        title: 'Reading a job number',
        summary:
          'A job number that starts with a word rather than digits is a placeholder. Two different locks apply depending on which placeholder it is, and knowing which one you are looking at usually answers "why is this grayed out".',
        table: {
          caption: 'What each job number means',
          headers: ['Job number', 'What it means', 'What you can do'],
          rows: [
            ['SYSTAC-014', 'A bid opportunity — not won yet', 'Read-only. Nothing can be edited.'],
            ['ClosedWon-007', 'Bid won, Sage has not issued a number yet', 'Edit the job freely — but no RFIs, submittals, daily logs, equipment, receiving, materials or change orders yet.'],
            ['10930', 'A real Sage Intacct job', 'Everything.'],
            ['IMP-12 / MNT-4', 'Import stub / maintenance placeholder', 'Everything — these are deliberately not locked.'],
          ],
        },
        watchOut: [
          'An amber banner across the top of every tab tells you which lock is active — **Locked · SYSTAC** or **Locked · No Sage #**.',
          'You will not see a won job still numbered SYSTAC. When Bid Tracker marks a bid won, the tracker renames it to the next **ClosedWon-** number in the same step, which is what lifts the read-only lock.',
        ],
      },
      {
        id: 'renumber-pill',
        title: 'The amber RENUMBER pill',
        summary:
          'A RENUMBER pill on the Projects list means the bid was won but the job is still carrying a placeholder number. It is a to-do, not an error — and it belongs to the estimator.',
        steps: [
          'The **estimator** sets the job up in Sage Intacct, which is where the real job number comes from.',
          'They then open the job here, go to **Overview ▸ Basics**, and replace the placeholder in **Job #** with that Sage number.',
          'On save, the pill disappears and the remaining activity locks lift for everyone.',
        ],
        watchOut: [
          'Because the number originates in Intacct, the estimator who created it there is the one who enters it here. If a job is stuck on a placeholder and the crew is blocked, chase the estimator rather than editing it yourself.',
          'Entering a real number is how the job is **adopted**. From that point Bid Tracker no longer controls its lifecycle — a later "closed lost" will not archive it.',
        ],
      },
    ],
  },

  {
    id: 'health-and-progress',
    title: 'How a job\'s status is judged',
    intro: 'Three signals appear all over the app. None of them are typed in by hand — all three are calculated.',
    topics: [
      {
        id: 'project-health',
        title: 'Project health: On track / At risk / Off track',
        summary:
          'Health is derived by the system from the job\'s dates and its mobilization status. Nobody sets it by hand, so there is nobody to ask to change it — you change it by fixing the dates or the mobilization status underneath.',
        table: {
          caption: 'What triggers each state — checked in this order, first match wins',
          headers: ['State', 'Triggered when'],
          rows: [
            ['Off track', 'Today is past the job\'s completion date and it isn\'t marked complete.'],
            ['Off track', 'Mobilization status is Delayed.'],
            ['At risk', 'Mobilization status is Off Track.'],
            ['At risk', 'The job is in Construction and its current start date moved more than 14 days later than the original start.'],
            ['On track', 'None of the above. Jobs marked Complete or Canceled always read on track.'],
          ],
        },
        watchOut: [
          'The job page tells you **which** of these fired, in plain words — open the health line on the Overview and use "How is this set?". You never have to guess which rule tripped.',
          'A re-mobilization that legitimately runs past the original completion date does **not** make the job read overdue — the later mobilization end supersedes the planned date.',
          'A job with no target completion date reads on track because the date checks cannot run at all. That is absence of information, not good news.',
          'A job that has not started yet is **not** judged on its start date moving. Re-planning a start in preconstruction is normal work, not a delay, so the slip check only applies once the job is in Construction.',
        ],
      },
      {
        id: 'mobilization-status',
        title: 'Mobilization status: the four values and what they mean',
        summary:
          'This one field answers two different questions — has mobilization happened, and is it going to plan. Getting it right matters beyond tidiness: health reads it, and so does the Readiness tile.',
        owner: OWNERSHIP.mobilizationAndChecklist,
        table: {
          headers: ['Value', 'Use it when'],
          rows: [
            ['On Track', 'Mobilization is still to come or under way, and going to plan.'],
            ['Mobilized', 'The crew is on site. Mobilization is finished — set this when it actually happens.'],
            ['Delayed', 'Mobilization has slipped. This makes the job read Off Track.'],
            ['Off Track', 'Mobilization is in trouble but not yet a confirmed delay. This makes the job read At Risk.'],
          ],
        },
        watchOut: [
          '**Mobilized is new and needs filling in.** Until recently there was no value meaning "the crew is on site", so most jobs — including finished ones — still say On Track. A job in Construction that isn\'t marked Mobilized shows a prompt on its Overview asking you to set it.',
          'Moving a job to Construction does **not** set this for you, on purpose. A job can be in Construction while mobilization genuinely lags, and stamping it automatically would hide exactly that.',
        ],
        deepLinkNote: 'Project workspace ▸ Overview ▸ Basics, next to Project Status.',
      },
      {
        id: 'checklist-dots',
        title: 'The row of colored dots',
        summary:
          'Each dot is one item on the job\'s mobilization checklist, in order. Hover any dot to see which item it is and where it stands. The count underneath is how many are finished.',
        table: {
          caption: 'Dot colors',
          headers: ['Color', 'Meaning'],
          rows: [
            ['Green', 'Yes — done'],
            ['Gray', 'N/A — does not apply to this job'],
            ['Red', 'No'],
            ['Orange', 'Blocked'],
            ['Pale gray', 'Not set yet'],
          ],
        },
        watchOut: [
          // Nested emphasis was `Yes *and* N/A` — the only formatting either
          // renderer supports is **bold**, and a lone `*` inside a bold span
          // breaks the match, so BOTH the page and the Word export printed the
          // asterisks literally. Caught by the export test, 2026-08-02.
          '**"Done" counts Yes AND N/A.** An item marked N/A is finished business, not an outstanding item — this is the single most misread thing on the portfolio.',
        ],
      },
      {
        id: 'readiness-score',
        title: 'The Readiness percentage',
        summary:
          'The Readiness tile scores how prepared the jobs that are about to start actually are. It is checklist completion weighted at 60% plus mobilization status at 40%.',
        watchOut: [
          'It covers **only jobs starting within the next two weeks** — the subtitle tells you how many that is. It is not a score for the whole portfolio.',
        ],
        deepLink: '/reports',
        access: { anyPermission: ['reports.view'], hideFromFieldLeaderOnly: true },
      },
    ],
  },

  {
    id: 'when-things-look-wrong',
    title: 'When something looks wrong',
    intro: 'The handful of situations that generate most of the "is this broken?" questions. Usually it is working as designed.',
    topics: [
      {
        id: 'costs-hidden',
        title: 'All the dollar figures disappeared',
        summary:
          'There is a single company-wide switch that hides cost information everywhere at once: the Financials and Commitments tabs vanish and every dollar column on Materials goes away.',
        watchOut: [
          'This is a global admin setting, not something about your account — it affects everyone simultaneously, including admins. If money vanished for the whole team at the same moment, this is why.',
          'Seeing Materials but no dollar columns is the classic symptom.',
        ],
      },
      // REMOVED 2026-07-28: a topic titled "A button is there, but saving fails"
      // described ungated action buttons on the company-wide Equipment page and
      // throughout Survey. That was fixed the same week (PR #227 aligned both
      // pages' UI gates to the API — EquipmentPage and SurveyPage now wrap their
      // actions in `canEdit`). Leaving it in would have told people a working
      // feature was broken and sent them to an admin for a permission they
      // already have, which is worse than saying nothing.
      {
        id: 'no-job-folder',
        title: 'I can\'t upload a file or a photo',
        summary:
          'File uploads go to the job\'s SharePoint Job Folder. If no folder is linked to the job, upload controls are replaced by a dashed placeholder.',
        steps: [
          'Go to the job\'s **Documents** tab.',
          'If no folder is linked, use **Create Job Folder** — or paste an existing folder URL on **Overview ▸ Job Info**.',
        ],
        watchOut: [
          'Uploads run under **your own** Microsoft account, not a service account. Being assigned to a job in the Project Tracker does not by itself give you access to its SharePoint folder — that has to be shared with you separately. This is the usual cause of a field leader being unable to add photos.',
          'Uploading a file is how it stays on the **record**. It is not how the file reaches someone outside E&W — Job Folder links do not open for outside parties, so emails to reviewers, GCs and architects need the files attached in Outlook.',
        ],
      },
      {
        id: 'export-scope',
        title: 'What the Excel export contains',
        summary:
          'Export Excel gives you exactly the rows on screen — the same filters, the same My/All scope, the same sort order, and archived jobs only if you have chosen to show them.',
        steps: [
          'Filter and sort the Projects list until it shows what you want.',
          'Click **⬇ Export Excel** — the tooltip confirms the row count before you click.',
          'To export everything, use **Clear filters** first — filters carry over from earlier in the session, so a smaller-than-expected export is usually one you set a while ago.',
        ],
        deepLink: '/projects',
        // The permission gate handles the real reader; the field-leader flag also
        // keeps it out of the Field Leader TRACK when an office user previews it.
        // Exporting the job list is an office capability — projects.export sits
        // with Admin and ProjectAdmin — so it is not part of a field leader's
        // world either way.
        access: { anyPermission: ['projects.export'], hideFromFieldLeaderOnly: true },
      },
    ],
  },

  {
    id: 'getting-help',
    title: 'Getting help and reporting problems',
    topics: [
      {
        id: 'send-feedback',
        title: 'The Feedback button',
        summary:
          'The 💬 Feedback button in the header is the fastest way to report a bug, suggest an idea, or ask a question. It reaches the team directly and is reviewed regularly, and any reply shows up in the same place.',
        steps: [
          'Click **💬 Feedback** in the top bar.',
          'Pick **Bug**, **Idea**, or **Question**.',
          'Describe it. For a bug, a screenshot helps enormously — press **Win+Shift+S**, draw a box, then paste straight into the message.',
          'As you type, **Similar requests** may appear under the box: things already on the board that read like yours. If one is what you mean, press **Me too** instead of sending it again. You’ll be told when it is answered, and what you typed stays in the box in case you still want to send it. **Not the same** hides a suggestion.',
          'Attach files if useful (up to 5, 15 MB each). Tick **Keep this private** if it is sensitive, then **Send**.',
          'To see what you have sent, what you said “me too” to, and any reply, open **💬 Feedback** and choose **My feedback**. A number on the button means something there is new, a reply or the item being closed, and opening the item clears it. You are also emailed when that happens, and the link in the email opens **My feedback**. To stop being told about something you said “me too” to, or were added to because an admin combined two requests (see below), open it there and press **Stop following…**, then confirm: it leaves the list, and if it is closed or off the board you cannot follow it again. The item you sent yourself cannot be stopped.',
          'To see what other people have asked, choose **Everyone’s feedback**. Open an item to read it and every reply, and press **Me too** if you want it as well. It then shows under **My feedback**, with any reply. **Undo** takes it back (on a request you were added to, the button reads **Stop following**). Open requests come first, and a closed one shows how it ended.',
          'If two requests ask for the same thing, an admin **combines** them. The duplicate closes and reads **Combined** under **My feedback**, with a note saying what happened, and everyone who followed it is added to the other request, which says why you follow it. Whoever sent the duplicate is emailed once (unless they are the admin who combined it); everyone else is not, and is told when the other request is answered. Open a combined item and press **Show that request** to go to the other one. To stop following the request you were added to, press **Stop following…** on it, as above.',
        ],
        watchOut: [
          'Your name, your role, and the page you were on are attached automatically for the admins — you do not need to describe where you were.',
          'The admins read what you send first. They may share it, and the replies to it, with everyone signed in on **Everyone’s feedback**; it is not there until an admin has chosen to share it. Your name is not shown with it, but a message can still give you away by what it says, so tick **Keep this private** for anything sensitive. Private feedback is read only by the admins, and nobody can put it on the board. Replies on the board show who wrote them.',
          'Because feedback is shared only when an admin chooses to, **Everyone’s feedback** can be short of items, and **Similar requests** may have little to suggest.',
          'An item you sent reads **Open** until it is closed, then **Done** or **Won’t do**, or just **Closed** when no reason was given. Feedback that was already closed before replies were added is not listed.',
        ],
      },
      {
        id: 'whats-new',
        title: 'What’s new',
        summary:
          'The ✨ What’s new button in the header lists recent changes to the Project Tracker that apply to your role, and links each one to the part of this guide that covers it. A number on the button is how many you haven’t seen.',
        steps: [
          'Click **✨ What’s new** in the top bar. The newest change is first, and the ones you haven’t seen are tagged **New**.',
          'Read the short summary. An entry may link to the screen it changes, and **What changed in the guide →** opens the topic in this guide that now covers it.',
          'Opening the list clears the number on the button. It remembers what you have seen in this browser, so on another computer the number starts again.',
          'The foot of the list shows this guide’s revision and when it was last reviewed, so you can tell whether the guide has changed since you last read it.',
        ],
        watchOut: [
          'You see only the changes that apply to your role, so a colleague in a different role may see a different list.',
        ],
      },
    ],
  },
];
